import type {Transport} from "@ots-frontend/ots-data";
import type {ActivityEntry} from "../../driver-activity/model/activity-types";

/** How many tickets the site panel shows, and asks the server for. */
export const SITE_TICKET_LIMIT = 3;
export const SITE_TICKET_DAYS = 7;

const DAY = 86_400_000;
const MINUTE = 60_000;

/**
 * The site panel's ticket search: the newest few at the site over the last week. Whole minutes keep the arguments,
 * and so the cache, steady between renders.
 */
export const siteTicketSearch = (siteId: string, now: number) => {
  const toDttm = Math.floor(now / MINUTE) * MINUTE;
  return {siteId, fromDttm: toDttm - SITE_TICKET_DAYS * DAY, toDttm, limit: SITE_TICKET_LIMIT};
};

export type SiteTicketLeg = {
  /** Picked up here, so the row points on to the delivery; otherwise it was delivered here. */
  outbound: boolean;
  /** The stop at this site, whose container and quantity the row shows. */
  here?: Transport;
  /** The other end of the haul. */
  there?: Transport;
};

/** Which end of a ticket is this site. A ticket that both picks up and delivers here reads as a pickup. */
export const siteTicketLeg = (entry: ActivityEntry, siteId: string): SiteTicketLeg => {
  const outbound = entry.pickup?.siteId === siteId || entry.delivery?.siteId !== siteId;
  return outbound
    ? {outbound, here: entry.pickup, there: entry.delivery}
    : {outbound, here: entry.delivery, there: entry.pickup};
};

/** The newest first, at most the panel's few; the server already limits, this keeps a stale cache honest. */
export const newestSiteTickets = (entries: readonly ActivityEntry[]) =>
  [...entries].sort((left, right) => (right.time ?? 0) - (left.time ?? 0)).slice(0, SITE_TICKET_LIMIT);
